import { FC, useCallback } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import cx from 'classnames';
import { Image } from 'antd';

import { RootState } from '../../../redux/store';
import { storeName as configModalName } from '../../../redux/models/configTour/configModal.model';
import { convertCurrencyVnd } from '../../../utils/functionUtils';
import { formatPriceProduct } from '../../../utils/KrPanoUtils';
import {
  ITEM_INFO_TEMPLATE,
  OVERVIEW_POSITION_POI,
  OVERVIEW_TYPE,
  POI_TYPE,
  SUPPORTED_OVERVIEW_POI,
  SUPPORTED_OVERVIEW_POI_ITEM_INFO
} from '../../constants/constants';
import PoiItemInfo from '../../../model/Poi/PoiItemInfo';
import PoiPromotion from '../../../model/Poi/PoiPromotion';
import { getImageObject } from '../../../utils/imageUtils';
import { FileModel } from '../../../types/CommonTypes';

import './styles.scss';

const HotspotOverview: FC = () => {
  const { previewedPoi } = useSelector((state: RootState) => state[configModalName]);
  const menuCollapsed = useSelector<RootState>((state) => state.app.layoutState.menuCollapsed);

  const getProductPrice = () => {
    let discount = '';

    if (previewedPoi?.itemInfo) {
      const { itemInfo: { price, currencyUnit, unit } } = previewedPoi;
      discount = formatPriceProduct(convertCurrencyVnd(price), currencyUnit, unit);
    }

    return { discount };
  };

  const getLocationDistance = () => {
    if (previewedPoi?.itemInfo) {
      const { itemInfo: { distance, distanceUnit } } = previewedPoi;

      return `${distance} ${distanceUnit}`;
    }

    return '';
  };

  const getSubtitle = useCallback(() => {
    let subTitle = '';

    if (previewedPoi) {
      if (previewedPoi.type === POI_TYPE.ITEM_INFO) {
        switch (previewedPoi.hoverType) {
          case ITEM_INFO_TEMPLATE.LOCATION:
            subTitle = getLocationDistance();
            break;

          case ITEM_INFO_TEMPLATE.PRODUCT:
            subTitle = getProductPrice().discount;
            break;

          default:
            console.warn(`Type[${previewedPoi.hoverType}] is not handled hover`);
            break;
        }
      }
    }

    return subTitle;
  }, [previewedPoi]);

  const getPoiName = useCallback(() => {
    let res = '';

    switch (previewedPoi.type) {
      case POI_TYPE.ITEM_INFO:
        res = (previewedPoi as PoiItemInfo).itemInfo?.name;
        break;

      case POI_TYPE.PROMOTION:
        res = (previewedPoi as PoiPromotion).promotion?.name;
        break;

      case POI_TYPE.TEXTURE:
        res = 'Swap element';
        break;

      default:
        break;
    }

    return res;
  }, [previewedPoi]);

  const isSupportedOverview = useCallback(() => {
    if (previewedPoi && SUPPORTED_OVERVIEW_POI.includes(previewedPoi.type)) {
      if (previewedPoi.type === POI_TYPE.ITEM_INFO) {
        const { itemInfo } = previewedPoi as PoiItemInfo;

        return SUPPORTED_OVERVIEW_POI_ITEM_INFO.includes(itemInfo?.type);
      }

      return true;
    }

    return false;
  }, [previewedPoi]);

  const isDisplayedOverview = useCallback(() => {
    return isSupportedOverview() && previewedPoi.overviewPosition !== OVERVIEW_POSITION_POI.NONE;
  }, [previewedPoi]);

  const getDescription = useCallback(() => {
    let res = '';

    if (previewedPoi.type === POI_TYPE.ITEM_INFO) {
      const { itemInfo: { description } } = previewedPoi as PoiItemInfo;
      res = description;
    } else if (previewedPoi.type === POI_TYPE.PROMOTION) {
      const { promotion: { endDate } } = previewedPoi as PoiPromotion;
      const dateFormat = moment(endDate).format('DD/MM/YYYY');
      res = `to ${dateFormat}`;
    }

    return res;
  }, [previewedPoi]);

  const hasViewMore = useCallback(() => {
    if (previewedPoi.type === POI_TYPE.ITEM_INFO) {
      const { itemInfo } = previewedPoi as PoiItemInfo;

      if (itemInfo.type === ITEM_INFO_TEMPLATE.LOCATION) {
        return false;
      }
    } else if (previewedPoi.type === POI_TYPE.TEXTURE) {
      return false;
    }

    return true;
  }, [previewedPoi]);

  const renderBody = () =>
    previewedPoi.overviewType === OVERVIEW_TYPE.IMAGE &&
    previewedPoi.type === POI_TYPE.PROMOTION ? (
      <Image
        preview={false}
        src={getImageObject((previewedPoi as PoiPromotion).promotion?.image as FileModel)?.url}
        className="cvtc-hotspot-overview-description__image"
      />
    ) : (
      <div className="cvtc-hotspot-overview__body">
        <Image
          preview={false}
          src={getImageObject((previewedPoi as PoiPromotion).promotion?.image as FileModel)?.url}
          className="cvtc-hotspot-overview__image-small"
        />
        <div className="cvtc-hotspot-overview-content">
          <div className="cvtc-hotspot-overview-content__title">{getPoiName()}</div>
          <div className="cvtc-hotspot-overview-content__price cvtc-hotspot-overview-price">
            {getSubtitle() ? (
              <div className="cvtc-hotspot-overview-price__discount">{getSubtitle()}</div>
            ) : null}
            <div className="cvtc-hotspot-overview-price__cost">{/* TODO: Original price */}</div>
          </div>
          <div className="cvtc-hotspot-overview-content__description cvtc-hotspot-overview-description">
            {getDescription() ? (
              <div className="cvtc-hotspot-overview-description__short">{getDescription()}</div>
            ) : null}
            {hasViewMore() ? (
              <div className="cvtc-hotspot-overview-description__view-more">View more</div>
            ) : null}
          </div>
        </div>
      </div>
    );

  if (!isDisplayedOverview()) {
    return null;
  }

  return (
    <div className={cx('cvtc-hotspot-overview', { 'cvtc-hotspot-overview--fly-right': !menuCollapsed })}>
      <div className="cvtc-hotspot-overview__header"></div>
      {previewedPoi ? renderBody() : null }
    </div>
  );
};

export default HotspotOverview;
